import React from 'react';
import isString from 'lodash/isString';
import classNameBind from 'classnames/bind';
import Icon from '../Icon';
import Tooltip from '../Tooltip';
import styles from './ActionList.module.css';
import itemAction from './methods/itemAction';

const bindClasses = classNameBind.bind(styles);

const NUMBER_OF_ELEMENTS_BEFORE_SELECTED = 1;

export default class ActionList extends React.PureComponent {
  wrapper = React.createRef();

  static defaultProps = {
    groups: [],
    onItemSelected: null,
    current: null,
    emptyListText: undefined,
    wordBreakingStyle: 'break-all',
    appearance: 'default',
    centerContent: false,
    hoverStyle: 'default',
    dataId: 'ActionList',
  }

  componentDidUpdate() {
    const { current } = this.props;
    const activeElement = this.wrapper.current.getElementsByClassName(bindClasses('active'))[0];

    if (current && activeElement !== undefined) {
      const topElement = this.untilNotExistOrLength(
        activeElement,
        NUMBER_OF_ELEMENTS_BEFORE_SELECTED,
      );

      this.wrapper.current.parentNode.scrollTop = topElement.offsetTop - topElement.parentNode.offsetTop;
    }
  }

  /**
   * Search for previous sibling up to provided length
   */
  untilNotExistOrLength = (elem, length) => {
    let result = elem;
    let times = 0;

    while (times < length) {
      if (result.previousSibling !== null) {
        result = result.previousSibling;
      } else {
        break;
      }
      times += 1;
    }

    return result;
  }

  renderEmptyText = () => {
    const { emptyListText, t } = this.props;
    if (typeof emptyListText === 'object') {
      return (
        <div className={bindClasses('noItemsAdvanced')}>
          <span className={bindClasses('noItems-title')}>{emptyListText.title}</span>
          <span className={bindClasses('noItems-info')}>{emptyListText.info}</span>
        </div>
      );
    }

    return (
      <div className={bindClasses('noItems')}>
        {emptyListText ? t(emptyListText) : t('actionlist.no-items-available')}
      </div>
    );
  }

  renderFavorites = (item) => {
    const { favoriteOnClick } = this.props;

    if (!favoriteOnClick) return null;

    return (
      <div
        className={bindClasses('favorite')}
        onClick={(el) => {
          el.stopPropagation();
          favoriteOnClick(item);
        }}
        role="button"
        tabIndex={-1}
        data-ppuc-id="ListSidebar.ListSidebarElement.Star"
      >
        {item.isFavorite
          ? <span><Icon name="star" color="yellow" /></span>
          : (
            <React.Fragment>
              <span className={bindClasses('favorite-not-selected', 'hovered')}><Icon name="star" color="white" /></span>
              <span className={bindClasses('favorite-not-selected', 'not-hovered')}><Icon name="star-empty" color="white" /></span>
            </React.Fragment>
          )
        }
      </div>
    );
  }

  render() {
    const {
      groups,
      onItemSelected,
      current,
      wordBreakingStyle,
      t,
      appearance,
      centerContent,
      hoverStyle,
      dataId,
    } = this.props;

    return (
      <nav
        className={bindClasses('wrapper', { fullHeight: groups.length === 0 }, `appearance-${appearance}`)}
        ref={this.wrapper}
        data-ppuc-id={dataId}
      >
        {groups.map((group, index) => (
          <div
            className={bindClasses('group')}
            key={group.id}
            data-ppuc-id={`${dataId}.group`}
          >
            {group.name && (
              <div className={bindClasses('groupName')} data-ppuc-id={`${dataId}.groupName`}>{group.name}</div>
            )}
            <ul
              className={bindClasses('items', { increasePadding: index && !group.name })}
              data-ppuc-id={`${dataId}.items`}
            >
              {group.items.map((item) => {
                const isActive = !item.disabled && current && current === item.id;

                return (
                  <li
                    key={item.id}
                    tabIndex={-1}
                    data-ppuc-id={item.dataId || `${dataId}.item`}
                    className={`${bindClasses(
                      'item',
                      `hoverStyle-${hoverStyle}`,
                      item.appearance && item.appearance,
                      { disabled: item.disabled, active: isActive },
                    )}`}
                  >
                    <Tooltip content={item.tooltip || null}>
                      <a
                        role="button"
                        tabIndex={-1}
                        className={bindClasses('link', { center: centerContent })}
                        style={{ wordBreak: wordBreakingStyle }}
                        {...itemAction(item, onItemSelected)}
                        data-ppuc-id={`${dataId}.link`}
                      >
                        {item.icon && (
                          <Icon
                            name={isString(item.icon) ? item.icon : item.icon.name}
                            color={item.icon.color}
                            size={appearance === 'condensed' ? 'small' : item.icon.size}
                          />
                        )}
                        {item.name}
                        {item.caption && <div className={bindClasses('caption')}>{item.caption}</div>}
                        {!item.favoriteDisabled && this.renderFavorites(item)}
                        {item.newBadge && (
                          <div className={bindClasses('new-badge')}>
                            <div className={bindClasses('text')}>
                              {t('actionlist.badge.new.label')}
                            </div>
                          </div>
                        )}
                        {item.betaBadge && (
                          <div className={bindClasses('new-badge')}>
                            <div className={bindClasses('text')}>
                              {t('actionlist.badge.beta.label')}
                            </div>
                          </div>
                        )}
                        {item.badge && (
                          <div className={bindClasses('new-badge')}>
                            <div className={bindClasses('text')}>
                              {item.badge}
                            </div>
                          </div>
                        )}
                      </a>
                    </Tooltip>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {groups.length === 0 && this.renderEmptyText()}
      </nav>
    );
  }
}
